/**
 * SignaturePad — reusable canvas signature capture component.
 * ─────────────────────────────────────────────────────────────────
 * Phase 5b. Designed for rinkside use on a phone or tablet.
 *
 * USAGE
 *   const padRef = useRef();
 *   ...
 *   <SignaturePad ref={padRef} onChange={(hasSignature) => ...} />
 *   <button onClick={async () => {
 *     const b64 = padRef.current?.getDataURL();
 *     if (!b64) return;
 *     await api.post(..., { signature_b64: b64 });
 *   }}>Save</button>
 *   <button onClick={() => padRef.current?.clear()}>Clear</button>
 *
 * IMPLEMENTATION NOTES
 *   • Uses Pointer Events (pointerdown/move/up) so a single code
 *     path handles touch, stylus, and mouse — no separate touch-
 *     event handlers needed. Modern browsers all support it.
 *   • Captures stroke points and re-renders on every move so the
 *     line is smooth (uses quadraticCurveTo from the midpoint of
 *     three consecutive points — the standard signature-pad
 *     smoothing trick).
 *   • Handles device pixel ratio: the canvas backing store is
 *     scaled to dpr * CSS pixels so strokes are crisp on retina.
 *   • setPointerCapture so a finger leaving the canvas mid-stroke
 *     still fires the pointerup; prevents stuck strokes.
 *   • touch-action: none on the canvas to prevent the page from
 *     scrolling under the finger.
 *
 * EXPORT FORMAT
 *   getDataURL() returns 'data:image/png;base64,...' (a full data
 *   URL). The backend accepts either the data URL or the raw
 *   base64 payload; we pass the data URL through so we can also
 *   render it back into an <img src> in the future for the
 *   printed scoresheet.
 */

import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Trash2 } from 'lucide-react';

const SignaturePad = forwardRef(function SignaturePad({ onChange, height = 160, label }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const pointsRef = useRef([]);  // current stroke
  const strokesRef = useRef([]); // committed strokes (array of arrays of {x,y})
  const [isEmpty, setIsEmpty] = useState(true);

  // Initialize canvas size to match container. Re-runs on resize.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = canvas.parentElement.clientWidth;
      const cssHeight = height;
      canvas.width  = cssWidth  * dpr;
      canvas.height = cssHeight * dpr;
      canvas.style.width  = cssWidth + 'px';
      canvas.style.height = cssHeight + 'px';
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'var(--text)';
      // strokeStyle from a CSS var isn't supported on all browsers —
      // fall back to an absolute color. Read the computed value once.
      const probe = document.createElement('div');
      probe.style.color = 'var(--text)';
      document.body.appendChild(probe);
      ctx.strokeStyle = getComputedStyle(probe).color || '#111';
      document.body.removeChild(probe);
      redraw();
    }
    resize();
    // Re-resize on window resize so a phone rotation does the right thing.
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  function redraw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    // Clear in backing-store coords; the ctx is already dpr-scaled
    // so clearRect uses CSS coords.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    for (const stroke of strokesRef.current) {
      drawStroke(ctx, stroke);
    }
    if (pointsRef.current.length > 0) {
      drawStroke(ctx, pointsRef.current);
    }
  }

  function drawStroke(ctx, pts) {
    if (pts.length < 2) {
      // A single tap — draw a dot.
      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(pts[0].x, pts[0].y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    // Quadratic smoothing via midpoints.
    for (let i = 1; i < pts.length - 1; i++) {
      const xc = (pts[i].x + pts[i + 1].x) / 2;
      const yc = (pts[i].y + pts[i + 1].y) / 2;
      ctx.quadraticCurveTo(pts[i].x, pts[i].y, xc, yc);
    }
    const last = pts[pts.length - 1];
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  }

  function getRelativePoint(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e) {
    e.preventDefault();
    drawingRef.current = true;
    pointsRef.current = [getRelativePoint(e)];
    try { canvasRef.current.setPointerCapture(e.pointerId); }
    catch (_e) { /* not supported on every browser; non-fatal */ }
    redraw();
  }
  function handlePointerMove(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    pointsRef.current.push(getRelativePoint(e));
    redraw();
  }
  function handlePointerUp(e) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (pointsRef.current.length > 0) {
      strokesRef.current.push(pointsRef.current);
      pointsRef.current = [];
      const wasEmpty = isEmpty;
      setIsEmpty(false);
      if (wasEmpty) onChange?.(true);
    }
    try { canvasRef.current.releasePointerCapture(e.pointerId); }
    catch (_e) { /* see above */ }
    redraw();
  }

  function clear() {
    strokesRef.current = [];
    pointsRef.current = [];
    setIsEmpty(true);
    onChange?.(false);
    redraw();
  }

  function getDataURL() {
    if (isEmpty) return null;
    return canvasRef.current?.toDataURL('image/png');
  }

  useImperativeHandle(ref, () => ({ clear, getDataURL, isEmpty: () => isEmpty }), [isEmpty]);

  return (
    <div>
      {label && (
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
          {label}
        </div>
      )}
      <div style={{
        position: 'relative',
        border: '1px solid var(--border)',
        borderRadius: 10,
        background: 'var(--surface-2)',
        overflow: 'hidden',
      }}>
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            display: 'block', width: '100%', height,
            // touch-action: none — prevents browser from scrolling
            // the page while a finger is drawing on the canvas. The
            // critical bit that makes this work on mobile.
            touchAction: 'none',
            // Faint guideline at the bottom of the box, like a paper
            // signature line. Pure CSS so it's not part of the
            // exported PNG.
            backgroundImage: 'linear-gradient(to bottom, transparent calc(100% - 24px), var(--border) calc(100% - 24px), var(--border) calc(100% - 23px), transparent calc(100% - 23px))',
            cursor: 'crosshair',
          }}
        />
        {isEmpty && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic',
          }}>
            Sign with your finger or stylus
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button type="button" onClick={clear}
          disabled={isEmpty}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'transparent', border: 'none',
            color: isEmpty ? 'var(--text-3)' : 'var(--text-2)',
            cursor: isEmpty ? 'default' : 'pointer',
            fontSize: 11, padding: '4px 8px',
          }}>
          <Trash2 size={10} />
          Clear
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
